import React, { useEffect, useState } from 'react';
import { User, getUsers, createUser, updateUser, deleteUser } from '../services/authService';
import { useAuth } from '../hooks/useAuth';

export default function UsersPage() {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form, setForm] = useState({ user_id: '', name: '', email: '', password: '', role: 'editor' });
  const [error, setError] = useState('');

  const loadUsers = async () => {
    try {
      setUsers(await getUsers());
    } catch {
      setError('Erro ao carregar usuários');
    }
  };

  useEffect(() => { loadUsers(); }, []);

  if (!isAdmin) return <div className="p-8 text-gray-600">Acesso restrito a administradores.</div>;

  const resetForm = () => {
    setForm({ user_id: '', name: '', email: '', password: '', role: 'editor' });
    setEditingUser(null);
    setShowForm(false);
    setError('');
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setForm({ user_id: user.user_id, name: user.name, email: user.email, password: '', role: user.role });
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (editingUser) {
        const data: any = { name: form.name, email: form.email, role: form.role };
        if (form.password) data.password = form.password;
        await updateUser(editingUser.user_id, data);
      } else {
        await createUser(form);
      }
      resetForm();
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleDelete = async (userId: string) => {
    if (!window.confirm(`Remover usuário "${userId}"?`)) return;
    try {
      await deleteUser(userId);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erro ao remover usuário');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          {!showForm && (
            <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">
              Novo Usuário
            </button>
          )}
        </div>

        {error && <div className="mb-4 p-3 text-sm text-red-700 bg-red-50 rounded">{error}</div>}

        {showForm && (
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-white rounded-lg shadow space-y-3">
            <h2 className="font-semibold text-gray-800">{editingUser ? 'Editar Usuário' : 'Novo Usuário'}</h2>
            {!editingUser && (
              <input placeholder="ID do usuário" value={form.user_id} onChange={e => setForm({...form, user_id: e.target.value})} required className="block w-full rounded border px-3 py-2" />
            )}
            <input placeholder="Nome" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="block w-full rounded border px-3 py-2" />
            <input placeholder="Email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="block w-full rounded border px-3 py-2" />
            <input placeholder={editingUser ? 'Nova senha (deixe vazio para manter)' : 'Senha'} type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editingUser} className="block w-full rounded border px-3 py-2" />
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="block w-full rounded border px-3 py-2">
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="contabilidade">Contabilidade</option>
              <option value="readonly">Somente Leitura</option>
            </select>
            <div className="flex gap-2">
              <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium">Salvar</button>
              <button type="button" onClick={resetForm} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 text-sm">Cancelar</button>
            </div>
          </form>
        )}

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 text-left text-sm text-gray-600">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Nome</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {users.map(u => (
                <tr key={u.user_id} className="text-sm hover:bg-blue-50 even:bg-gray-100">
                  <td className="px-4 py-3 font-mono">{u.user_id}</td>
                  <td className="px-4 py-3">{u.name}</td>
                  <td className="px-4 py-3">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-700' :
                      u.role === 'editor' ? 'bg-blue-100 text-blue-700' :
                      u.role === 'contabilidade' ? 'bg-emerald-100 text-emerald-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{u.role}</span>
                  </td>
                  <td className="px-4 py-3 space-x-2">
                    <button onClick={() => handleEdit(u)} className="text-blue-600 hover:underline text-sm">Editar</button>
                    <button onClick={() => handleDelete(u.user_id)} className="text-red-600 hover:underline text-sm">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
